/* ==========================================================================
   WAQT — theme JavaScript
   Custom elements + delegated handlers. No framework, no build step.
   ========================================================================== */

(function () {
  'use strict';

  var routes = window.routes || {};
  var settings = window.themeSettings || {};
  var strings = window.themeStrings || {};

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(cents) {
    var amount = (cents / 100).toLocaleString('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return (settings.moneyFormat || 'Rs {{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || response.statusText);
        return data;
      });
    });
  }

  /* ---------------------------------------------------------------- Ticker */

  class RotatingTicker extends HTMLElement {
    connectedCallback() {
      this.items = this.querySelectorAll('.ticker__item');
      if (this.items.length < 2 || reduceMotion) return;

      this.index = 0;
      var interval = parseInt(this.dataset.interval, 10) || 4000;
      this.timer = setInterval(this.advance.bind(this), interval);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    advance() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }

  customElements.define('rotating-ticker', RotatingTicker);


  /* ----------------------------------------------------------- Pinned header */

  (function pinnedHeader() {
    var group = document.querySelector('[data-header-group]');
    if (!group) return;

    var ticker = group.querySelector('.ticker');

    // The group is fixed, so the page needs to reserve its height. Measure it
    // rather than hard-coding: the ticker is optional and its height varies.
    // offsetHeight ignores transforms, so this stays correct while the group is
    // slid up to hide the ticker.
    function measure() {
      var root = document.documentElement;
      root.style.setProperty('--header-h', group.offsetHeight + 'px');
      root.style.setProperty('--ticker-h', (ticker ? ticker.offsetHeight : 0) + 'px');
    }

    // Only sit transparently on top of a section that asked for it — over a
    // white collection page white-on-white type would be invisible.
    var overlayTarget = document.querySelector('[data-header-overlay]');
    if (overlayTarget) group.classList.add('header-group--overlay');

    var threshold = 12;
    var ticking = false;
    var lastY = window.scrollY;

    // Ignore sub-pixel and momentum jitter, otherwise the ticker flickers as the
    // direction flips back and forth on a trackpad.
    var DIRECTION_DELTA = 6;

    function onScroll() {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(function () {
        var y = window.scrollY;
        var atTop = y <= threshold;
        var delta = y - lastY;

        group.classList.toggle('is-scrolled', !atTop);

        // Shown at the very top, and whenever the reader scrolls back up.
        if (atTop) {
          group.classList.remove('is-ticker-hidden');
        } else if (Math.abs(delta) >= DIRECTION_DELTA) {
          group.classList.toggle('is-ticker-hidden', delta > 0);
        }

        if (Math.abs(delta) >= DIRECTION_DELTA) lastY = y;
        ticking = false;
      });
    }

    measure();
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });

    if (window.ResizeObserver) new ResizeObserver(measure).observe(group);

    // Section groups re-render in the theme editor; re-measure when they do.
    document.addEventListener('shopify:section:load', function () {
      measure();
      onScroll();
    });
  })();

  /* ----------------------------------------------------------------- Motion */

  /* Site-wide scroll choreography.
     The hidden state is applied by JS (`motion-ready` on <html>) and never by
     CSS alone — so if this script fails, or the visitor asks for reduced
     motion, every element is simply visible. Nothing can be animated into
     permanent invisibility. */

  (function motion() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var root = document.documentElement;

    // What gets revealed, in the order they should feel. Anything inside the
    // header, a drawer or the hero is excluded — those have their own timing.
    var TARGETS = [
      '.section-head',
      '.editorial__line',
      '.tile',
      '.card',
      '.review',
      '.services__item',
      '.story__media', '.story__copy',
      '.feature__copy',
      '.rooms__list', '.rooms__media',
      '.collection-hero__title', '.collection-hero__desc',
      '.pdp__frame', '.pdp__info > *',
      '.pdp-description__inner',
      '.footer__col'
    ].join(',');

    var EXCLUDE = '.header-group, .drawer, .hero, .lightbox, .megamenu, [data-no-motion]';

    var seen = [];
    document.querySelectorAll(TARGETS).forEach(function (el) {
      if (el.closest(EXCLUDE)) return;
      if (el.hasAttribute('data-animate')) return;
      // Skip anything whose ancestor is already animating — nested reveals
      // read as jitter, not choreography.
      if (el.parentElement && el.parentElement.closest('[data-animate]')) return;

      el.setAttribute('data-animate', '');
      seen.push(el);
    });

    if (!seen.length) return;

    // Stagger by position within the same parent, so a row of cards cascades
    // but two unrelated blocks do not wait on each other.
    var counts = new Map();
    seen.forEach(function (el) {
      var parent = el.parentElement;
      var n = counts.get(parent) || 0;
      counts.set(parent, n + 1);
      if (n > 0) el.style.setProperty('--motion-delay', Math.min(n, 6) * 70 + 'ms');
    });

    root.classList.add('motion-ready');

    function reveal(el) {
      el.classList.add('is-in');
      observer.unobserve(el);
      var at = seen.indexOf(el);
      if (at !== -1) seen.splice(at, 1);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) reveal(entry.target);   // one-shot
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });

    seen.slice().forEach(function (el) { observer.observe(el); });

    /* Backstop. An IntersectionObserver that misses a callback would leave
       content at opacity 0 permanently — the one failure this whole layer must
       never have. A cheap bounding-box sweep on scroll catches anything the
       observer did not, and costs nothing once `seen` empties. */
    /* Throttled on a timestamp, NOT requestAnimationFrame. An rAF-gated flag
       that never gets its callback would latch on and kill the backstop for
       good — which is precisely the failure it exists to catch. Reading a few
       bounding boxes every 100ms is cheap enough to do outright. */
    var lastSweep = 0;
    function sweep() {
      if (!seen.length) {
        window.removeEventListener('scroll', sweep);
        window.removeEventListener('resize', sweep);
        return;
      }

      var now = Date.now();
      if (now - lastSweep < 100) return;
      lastSweep = now;

      seen.slice().forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight * 0.94) reveal(el);
      });
    }

    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep, { passive: true });

    // Whatever is already on screen reveals now, not on a scroll that may
    // never come. Timeout rather than rAF so it runs even in a background tab.
    setTimeout(function () {
      seen.slice().forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight * 0.94) reveal(el);
      });
    }, 60);
  })();

  /* ---------------------------------------------------------------- Drawers */

  var Drawers = {
    open: function (name) {
      var drawer = document.querySelector('[data-drawer="' + name + '"]');
      var scrim = document.querySelector('[data-scrim="' + name + '"]');
      if (!drawer) return;

      // Capture the opener BEFORE closing anything: close() restores focus to
      // the previous drawer's opener, which would otherwise become what we
      // record here and send focus to the wrong button on the way back out.
      var opener = document.activeElement;

      // Three drawers share this controller now (nav, cart, wishlist) and they
      // all dock to the same edges — opening one has to dismiss the last, or
      // they stack and the scrims double up.
      if (this.active && this.active !== name) this.close(this.active);

      drawer.classList.add('is-open');
      if (scrim) scrim.classList.add('is-open');
      document.body.classList.add('no-scroll');

      document.querySelectorAll('[data-drawer-open="' + name + '"]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });

      this.active = name;
      this.lastFocus = opener;
      drawer.setAttribute('tabindex', '-1');
      drawer.focus();
    },

    close: function (name) {
      name = name || this.active;
      var drawer = document.querySelector('[data-drawer="' + name + '"]');
      var scrim = document.querySelector('[data-scrim="' + name + '"]');
      if (!drawer) return;

      drawer.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      document.body.classList.remove('no-scroll');

      document.querySelectorAll('[data-drawer-open="' + name + '"]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });

      if (this.lastFocus) this.lastFocus.focus();
      this.active = null;
    }
  };

  document.addEventListener('click', function (event) {
    var openBtn = event.target.closest('[data-drawer-open]');
    if (openBtn) {
      event.preventDefault();
      Drawers.open(openBtn.dataset.drawerOpen);
      return;
    }

    var closeBtn = event.target.closest('[data-drawer-close]');
    if (closeBtn) {
      event.preventDefault();
      Drawers.close(closeBtn.dataset.drawerClose);
      return;
    }

    var scrim = event.target.closest('[data-scrim]');
    if (scrim) Drawers.close(scrim.dataset.scrim);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && Drawers.active) Drawers.close();
  });

  /* -------------------------------------------------------------- Mega menu */

  var megaRoot = document.querySelector('[data-megamenu-root]');
  var megaPanel = document.querySelector('[data-megamenu]');

  if (megaRoot && megaPanel) {
    var megaTitle = megaPanel.querySelector('[data-mega-title]');
    var closeTimer;

    var showPanel = function (id, title) {
      clearTimeout(closeTimer);

      megaPanel.querySelectorAll('[data-mega-panel]').forEach(function (panel) {
        panel.classList.toggle('is-active', panel.id === id);
      });
      megaRoot.querySelectorAll('[data-mega-trigger]').forEach(function (trigger) {
        trigger.classList.toggle('is-open', trigger.dataset.megaTrigger === id);
      });

      if (megaTitle) megaTitle.textContent = title;
      megaPanel.classList.add('is-open');
    };

    var hidePanel = function () {
      closeTimer = setTimeout(function () {
        megaPanel.classList.remove('is-open');
        megaRoot.querySelectorAll('[data-mega-trigger]').forEach(function (trigger) {
          trigger.classList.remove('is-open');
        });
      }, 120);
    };

    megaRoot.querySelectorAll('[data-mega-trigger]').forEach(function (trigger) {
      var panel = document.getElementById(trigger.dataset.megaTrigger);
      var title = panel ? panel.dataset.title : trigger.textContent.trim();

      trigger.addEventListener('mouseenter', function () {
        showPanel(trigger.dataset.megaTrigger, title);
      });
      trigger.addEventListener('focus', function () {
        showPanel(trigger.dataset.megaTrigger, title);
      });
    });

    megaRoot.addEventListener('mouseleave', hidePanel);
    megaPanel.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    megaPanel.addEventListener('mouseleave', hidePanel);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') hidePanel();
    });
  }

  /* ------------------------------------------------------------- Hero slides */

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = this.querySelectorAll('[data-slide]');
      this.copies = this.querySelectorAll('[data-slide-copy]');
      this.dots = this.querySelectorAll('[data-slide-to]');
      this.index = 0;

      var self = this;
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          self.goTo(parseInt(dot.dataset.slideTo, 10));
          self.restart();
        });
      });

      this.bindTray();

      if (this.slides.length > 1 && !reduceMotion) this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    restart() {
      clearInterval(this.timer);
      var interval = parseInt(this.dataset.interval, 10) || 7000;
      var self = this;
      this.timer = setInterval(function () {
        self.goTo((self.index + 1) % self.slides.length);
      }, interval);
    }

    goTo(next) {
      this.index = next;

      this.slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === next);
      });
      this.copies.forEach(function (copy, i) {
        copy.hidden = i !== next;
      });
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === next);
      });
    }

    bindTray() {
      var toggle = this.querySelector('[data-tray-toggle]');
      var tray = this.querySelector('[data-tray]');
      var glyph = this.querySelector('[data-tray-glyph]');
      if (!toggle || !tray) return;

      toggle.addEventListener('click', function () {
        var open = tray.hidden;
        tray.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        if (glyph) glyph.textContent = open ? '−' : '+';
      });
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);

  /* --------------------------------------------------------------- Carousel */

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      var scope = this.closest('.section-carousel') || document;
      var prev = scope.querySelector('[data-carousel-prev]');
      var next = scope.querySelector('[data-carousel-next]');
      var self = this;

      var step = function (direction) {
        self.scrollBy({ left: direction * self.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
      };

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
    }
  }

  customElements.define('product-carousel', ProductCarousel);

  /* ----------------------------------------------------------- Shop by room */

  class ShopByRoom extends HTMLElement {
    connectedCallback() {
      this.links = this.querySelectorAll('[data-room]');
      this.lefts = this.querySelectorAll('[data-room-left]');
      this.rights = this.querySelectorAll('[data-room-right]');

      var self = this;
      this.links.forEach(function (link) {
        var activate = function () { self.select(parseInt(link.dataset.room, 10)); };
        link.addEventListener('mouseenter', activate);
        link.addEventListener('focus', activate);
      });

      this.select(0);
    }

    select(index) {
      this.links.forEach(function (link, i) {
        link.classList.toggle('is-active', i === index);
      });
      this.lefts.forEach(function (img, i) {
        img.classList.toggle('is-active', i === index);
      });
      this.rights.forEach(function (img, i) {
        img.classList.toggle('is-active', i === index);
      });
    }
  }

  customElements.define('shop-by-room', ShopByRoom);


  /* --------------------------------------------------------- Variant picker */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variant-data]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    get selected() {
      var values = [];
      this.querySelectorAll('.vp__input:checked').forEach(function (input) {
        values[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      return values;
    }

    match(values) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (option, i) { return option === values[i]; });
      });
    }

    onChange() {
      var values = this.selected;
      var variant = this.match(values);

      values.forEach(function (value, i) {
        var label = this.querySelector('[data-option-label="' + i + '"]');
        if (label) label.textContent = value;
      }, this);

      this.updateAvailability();
      this.updateUrl(variant);
      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
    }

    // Grey out values that pair with nothing in stock, given the other choices.
    updateAvailability() {
      var values = this.selected;
      var variants = this.variants;

      this.querySelectorAll('.vp__input').forEach(function (input) {
        var index = parseInt(input.dataset.optionIndex, 10);
        var candidate = values.slice();
        candidate[index] = input.value;

        var reachable = variants.some(function (variant) {
          return candidate.every(function (value, i) {
            return i === index ? variant.options[i] === value : variant.options[i] === value;
          }) && variant.available;
        });

        input.disabled = !reachable;
      });
    }

    updateUrl(variant) {
      if (!variant) return;
      window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
    }

    updateForm(variant) {
      var input = document.querySelector('[data-variant-id]');
      if (input) input.value = variant ? variant.id : '';

      document.querySelectorAll('[data-add-to-cart]').forEach(function (button) {
        button.disabled = !variant || !variant.available;
        var label = button.querySelector('[data-add-to-cart-text]');
        if (!label) return;
        label.textContent = !variant ? strings.unavailable
          : (variant.available ? strings.addToCart : strings.soldOut);
      });
    }

    updatePrice(variant) {
      var block = document.querySelector('[data-price-block]');
      if (!block || !variant) return;

      var now = block.querySelector('.price-row__now');
      var was = block.querySelector('.price-row__was');
      var off = block.querySelector('.price-row__off');

      if (now) now.textContent = money(variant.price);

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (was) {
        was.textContent = onSale ? money(variant.compare_at_price) : '';
        was.hidden = !onSale;
      }
      if (off) {
        var pct = onSale
          ? Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100)
          : 0;
        off.textContent = pct ? '-' + pct + '%' : '';
        off.hidden = !pct;
      }
      block.querySelector('.price-row').classList.toggle('price-row--plain', !onSale);
    }

    // Scroll the chosen variant's image into view in the stacked gallery.
    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      var media = document.querySelectorAll('.pdp__media img, .pdp__media video');
      var target = media[variant.featured_media.position - 1];
      if (target && !reduceMotion) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* ----------------------------------------------------------- Scroll reveal */

  class ScrollReveal extends HTMLElement {
    connectedCallback() {
      this.words = this.querySelectorAll('.reveal__word');
      if (!this.words.length) return;

      if (reduceMotion) {
        this.words.forEach(function (word) { word.classList.add('is-lit'); });
        return;
      }

      this.onScroll = this.paint.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.paint();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    paint() {
      var box = this.getBoundingClientRect();
      var vh = window.innerHeight;
      var progress = Math.min(1, Math.max(0, (vh * 0.85 - box.top) / (box.height + vh * 0.35)));
      var lit = Math.floor(progress * this.words.length * 1.15);

      this.words.forEach(function (word, i) {
        word.classList.toggle('is-lit', i < lit);
      });
    }
  }

  customElements.define('scroll-reveal', ScrollReveal);



  /* -------------------------------------------------------------- Wishlist */

  /* Ported from the Watch Winders favourites drawer. Each saved entry carries
     everything a row needs, so the drawer paints straight from localStorage
     with no fetch. Open/close is delegated to the shared Drawers controller. */

  (function wishlist() {
    var KEY = 'waqt:saved';

    var drawer = document.querySelector('[data-drawer="wishlist"]');

    // The drawer and the Favourites page share this controller, so every hook
    // is queried document-wide and painted as a set. Either surface can be
    // absent; both can be present at once.
    function all(selector) {
      return Array.prototype.slice.call(document.querySelectorAll(selector));
    }

    function read() {
      try {
        var list = JSON.parse(localStorage.getItem(KEY)) || [];
        // v1 stored bare handles. Those cannot render a row, so drop them
        // rather than showing a blank entry nobody can act on.
        return list.filter(function (item) { return item && typeof item === 'object'; });
      } catch (e) {
        return [];   // private mode / storage disabled
      }
    }

    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }

    function has(list, id) {
      return list.some(function (item) { return String(item.id) === String(id); });
    }

    function esc(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        .replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function rowMarkup(item) {
      return '<div class="wishlist__row" data-wishlist-row="' + esc(item.id) + '">' +
        '<a class="wishlist__media" href="' + esc(item.url) + '">' +
          (item.image ? '<img src="' + esc(item.image) + '" alt="" loading="lazy">' : '') +
        '</a>' +
        '<div class="wishlist__body">' +
          (item.brand ? '<span class="wishlist__brand">' + esc(item.brand) + '</span>' : '') +
          '<a class="wishlist__name" href="' + esc(item.url) + '">' + esc(item.title) + '</a>' +
          '<span class="wishlist__price">' + esc(item.price) + '</span>' +
        '</div>' +
        '<div class="wishlist__acts">' +
          '<button type="button" class="wishlist__btn wishlist__btn--remove" ' +
            'data-wishlist-remove="' + esc(item.id) + '" ' +
            'aria-label="' + esc(strings.wishlistRemove || 'Remove') + '">&#10005;</button>' +
        '</div>' +
      '</div>';
    }

    // The page shows real cards rather than drawer rows — same data, the
    // structure differs because a page has room and a drawer does not.
    function cardMarkup(item) {
      return '<figure class="card" data-wishlist-row="' + esc(item.id) + '">' +
        '<div class="card__media">' +
          '<a href="' + esc(item.url) + '" class="card__media-link">' +
            '<div class="card__images">' +
              (item.image
                ? '<img class="card__img card__img--primary" src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy">'
                : '') +
            '</div>' +
          '</a>' +
          '<button type="button" class="card__save is-saved" data-wish="' + esc(item.id) + '" ' +
            'data-wish-variant="' + esc(item.variant) + '" data-wish-title="' + esc(item.title) + '" ' +
            'data-wish-url="' + esc(item.url) + '" data-wish-image="' + esc(item.image) + '" ' +
            'data-wish-price="' + esc(item.price) + '" data-wish-brand="' + esc(item.brand) + '" ' +
            'aria-pressed="true" aria-label="' + esc(strings.wishlistRemove || 'Remove') + '">' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
            '<path d="M12 20s-7-4.5-7-9.2A4 4 0 0 1 12 8a4 4 0 0 1 7 2.8C19 15.5 12 20 12 20z"/></svg>' +
          '</button>' +
          (item.variant
            ? '<button type="button" class="card__add-round" data-quick-add data-variant-id="' + esc(item.variant) + '" ' +
              'aria-label="' + esc(strings.addToCart || 'Add to cart') + '">' +
              '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
              '<path d="M6 7h12l-1 12H7L6 7z"/><path d="M9 7a3 3 0 0 1 6 0"/></svg></button>'
            : '') +
        '</div>' +
        '<figcaption class="card__body">' +
          '<a href="' + esc(item.url) + '" class="card__name h-card-sm">' + esc(item.title) + '</a>' +
          '<span class="price-row price-row--plain"><span class="price-row__now">' + esc(item.price) + '</span></span>' +
        '</figcaption>' +
      '</figure>';
    }

    function renderRows(list) {
      var empty = !list.length;

      all('[data-wishlist-list]').forEach(function (el) {
        el.innerHTML = empty ? '' : list.map(rowMarkup).join('');
      });

      all('[data-wishlist-grid]').forEach(function (el) {
        el.innerHTML = empty ? '' : list.map(cardMarkup).join('');
      });

      all('[data-wishlist-empty]').forEach(function (el) {
        el.classList.toggle('is-shown', empty);
      });

      all('[data-wishlist-foot]').forEach(function (el) {
        if (empty) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
      });
    }

    // Anything painted after this controller booted (the recently-viewed rail)
    // asks for a re-stamp rather than duplicating the saved-state logic.
    document.addEventListener('waqt:resync', function () { syncHearts(read()); });

    function syncHearts(list) {
      document.querySelectorAll('[data-wish]').forEach(function (button) {
        var on = has(list, button.dataset.wish);
        button.classList.toggle('is-saved', on);
        button.setAttribute('aria-pressed', String(on));
      });
    }

    function syncCount(list) {
      document.querySelectorAll('[data-wishlist-count]').forEach(function (element) {
        element.textContent = list.length;
        element.classList.toggle('is-hidden', list.length === 0);
      });
      all('[data-wishlist-count-label]').forEach(function (element) {
        element.textContent = list.length;
      });
    }

    function paint() {
      var list = read();
      syncCount(list);
      syncHearts(list);
      renderRows(list);
    }

    document.addEventListener('click', function (event) {
      var heart = event.target.closest('[data-wish]');
      if (heart) {
        event.preventDefault();
        var list = read();

        if (has(list, heart.dataset.wish)) {
          list = list.filter(function (item) {
            return String(item.id) !== String(heart.dataset.wish);
          });
        } else {
          list.push({
            id:      heart.dataset.wish,
            variant: heart.dataset.wishVariant || '',
            title:   heart.dataset.wishTitle || '',
            url:     heart.dataset.wishUrl || '#',
            image:   heart.dataset.wishImage || '',
            price:   heart.dataset.wishPrice || '',
            brand:   heart.dataset.wishBrand || ''
          });
        }

        write(list);
        paint();
        return;
      }

      var remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        write(read().filter(function (item) {
          return String(item.id) !== String(remove.dataset.wishlistRemove);
        }));
        paint();
      }
    });

    // Add everything to the cart in one request, then hand over to the cart
    // drawer — the wishlist is a staging area, not a second cart.
    all('[data-wishlist-add-all]').forEach(function (addAll) {
      var addAllText = addAll.querySelector('[data-wishlist-add-all-text]');

      addAll.addEventListener('click', function () {
        var items = read()
          .filter(function (item) { return item.variant; })
          .map(function (item) { return { id: item.variant, quantity: 1 }; });

        if (!items.length) return;

        addAll.disabled = true;
        if (addAllText) addAllText.textContent = strings.wishlistAdding || 'Adding…';

        function done() {
          addAll.disabled = false;
          if (addAllText) addAllText.textContent = strings.wishlistAddAll || 'Add all to cart';
        }

        postJSON(routes.cart_add_url + '.js', { items: items })
          .then(function () {
            return fetch(routes.cart_url + '.js').then(function (r) { return r.json(); });
          })
          .then(function (cart) {
            Drawers.close('wishlist');
            return afterCartChange(cart, true);
          })
          .catch(function (error) { window.alert(error.message || strings.cartError); })
          .finally(done);
      });
    });

    paint();
    document.addEventListener('shopify:section:load', paint);

    // Another tab changed the list — keep this one honest.
    window.addEventListener('storage', function (event) {
      if (event.key === KEY) paint();
    });
  })();

  /* ----------------------------------------------------- PDP mobile gallery */

  (function pdpMobile() {
    var media  = document.querySelector('[data-pdp-media]');
    var thumbs = document.querySelector('[data-pdp-thumbs]');
    var bar    = document.querySelector('[data-pdp-bar]');
    var buy    = document.querySelector('[data-pdp-buy]');
    if (!media && !bar) return;

    var behavior = reduceMotion ? 'auto' : 'smooth';

    // Thumbnails drive the scroll-snap strip.
    if (media && thumbs) {
      var slides  = Array.prototype.slice.call(media.children);
      var buttons = Array.prototype.slice.call(thumbs.querySelectorAll('[data-pdp-thumb]'));

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          var slide = slides[parseInt(button.dataset.pdpThumb, 10)];
          // block:'nearest' keeps this from scrolling the page vertically as
          // well as the strip horizontally.
          if (slide) slide.scrollIntoView({ behavior: behavior, block: 'nearest', inline: 'start' });
        });
      });

      if ('IntersectionObserver' in window) {
        var strip = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.intersectionRatio < 0.6) return;
            var index = slides.indexOf(entry.target);
            buttons.forEach(function (b, i) { b.classList.toggle('is-active', i === index); });
          });
        }, { root: media, threshold: [0.6] });

        slides.forEach(function (slide) { strip.observe(slide); });
      }
    }

    // Publish the bar's real height so the sticky reel and the WhatsApp float
    // can clear it instead of sitting on top of the Add to cart button. It is
    // measured, not assumed: the height is content plus
    // env(safe-area-inset-bottom), which differs per device. On desktop the bar
    // stays display:none and this reads 0, so nothing is offset there.
    if (bar) {
      var publishBarHeight = function () {
        document.documentElement.style.setProperty('--pdp-bar-h', bar.offsetHeight + 'px');
      };
      publishBarHeight();
      if ('ResizeObserver' in window) new ResizeObserver(publishBarHeight).observe(bar);
      else window.addEventListener('resize', publishBarHeight);
    }

    // Sticky bar appears only once the real buy block has scrolled ABOVE the
    // viewport — not while it is still below and simply not reached yet.
    if (bar && buy && 'IntersectionObserver' in window) {
      var watcher = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', scrolledPast);
        });
      }, { threshold: 0 });

      watcher.observe(buy);
    }
  })();

  /* --------------------------------------------------------- Lazy videos */

  /* `autoplay` in the markup downloads the whole file whether or not the
     visitor ever reaches it. The signature reel is 3.4 MB and sits most of a
     page down. Play it when it arrives on screen instead, and pause it when it
     leaves so it is not decoding behind the footer. */

  (function lazyVideos() {
    var videos = Array.prototype.slice.call(document.querySelectorAll('.js-lazy-video'));
    if (!videos.length || !('IntersectionObserver' in window)) return;

    var seen = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting && !reduceMotion) video.play().catch(function () {});
        else video.pause();
      });
    }, { threshold: 0.15 });

    videos.forEach(function (video) { seen.observe(video); });
  })();

  /* ----------------------------------------------------- Shoppable videos */

  /* A centred rail of 9:16 films that opens into a portrait player.

     Two rules carried over from the sticky reel, for the same reasons:
       - the <video> is MOVED into the stage, never cloned, so the buffer and
         playback position survive and only one element per film exists;
       - never call play() without a catch(), and never let that catch set a
         property another code path owns. */

  (function shoppableVideos() {
    var root = document.querySelector('[data-shoppable]');
    if (!root) return;

    var scroll = root.querySelector('[data-svr-scroll]');
    var cards  = Array.prototype.slice.call(root.querySelectorAll('[data-svr-card]'));
    var player = root.querySelector('[data-svr-player]');
    var stage  = root.querySelector('[data-svr-stage]');
    var plaque = root.querySelector('[data-svr-plaque]');
    var bar    = root.querySelector('[data-svr-progress]');
    var sound  = root.querySelector('[data-svr-sound]');
    var steps  = root.querySelector('.svr-player__steps');
    if (!scroll || !cards.length || !player || !stage) return;

    /* Shopify's video_tag emits a fallback <img> for the poster and gives it no
       alt, which fails the image-alt audit four times over. Empty alt is the
       correct value, not a description: the plaque underneath already names the
       product, so the poster is decorative. */
    root.querySelectorAll('video img:not([alt])').forEach(function (img) { img.alt = ''; });

    var active = -1;                       // index open in the player, -1 = closed

    /* Hold the element references. Because the <video> is MOVED into the stage,
       re-querying `cards[i].querySelector('.svr__video')` returns null for
       whichever film is currently open — which silently broke the progress bar,
       the mute toggle and detach(), leaving videos piled up in the stage. Any
       move-not-clone design has to keep handles, never look up by position. */
    var videos = cards.map(function (card) { return card.querySelector('.svr__video'); });
    var frames = cards.map(function (card) { return card.querySelector('.svr__frame'); });

    function videoOf(i) { return videos[i] || null; }
    function frameOf(i) { return frames[i] || null; }

    function esc(v) {
      return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        .replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /* ---- rail: only play what is on screen ---- */

    // Autoplaying every film at once is the expensive way to build this. Offscreen
    // ones stay paused, so a rail of eight costs about what one costs.
    if ('IntersectionObserver' in window) {
      var seen = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var index = cards.indexOf(entry.target);
          if (index < 0 || index === active) return;   // active one lives in the player
          var video = videoOf(index);
          if (!video) return;
          if (entry.isIntersecting && !reduceMotion) video.play().catch(function () {});
          else video.pause();
        });
      /* root MUST be the viewport, not `scroll`.
         Rooting it to the horizontal scroller measured only whether a card was
         scrolled into view SIDEWAYS, and reported the first two cards as
         intersecting from the moment the page loaded, while the whole section
         was still far below the fold. Both autoplayed and downloaded in full:
         4.4 MB + 4.4 MB on a mobile connection, before the visitor had scrolled
         anywhere near them. Lighthouse measured a 15 MB homepage because of it.

         With the viewport as root, the intersection rect is clipped by ancestor
         overflow too, so this still correctly pauses cards scrolled out of the
         rail horizontally. One root handles both axes. */
      }, { threshold: 0.25 });
      cards.forEach(function (card) { seen.observe(card); });
    }

    /* ---- rail: arrows ---- */

    function syncArrows() {
      var max = scroll.scrollWidth - scroll.clientWidth;
      var fits = max <= 1;
      var prev = root.querySelector('[data-svr-prev]');
      var next = root.querySelector('[data-svr-next]');
      // Disabled rather than removed: a control that vanishes on resize reflows
      // the section under whoever is reading it.
      if (prev) prev.disabled = fits || scroll.scrollLeft <= 1;
      if (next) next.disabled = fits || scroll.scrollLeft >= max - 1;
    }

    function step(direction) {
      var card = cards[0];
      var by = card ? card.getBoundingClientRect().width + 12 : scroll.clientWidth * 0.8;
      scroll.scrollBy({ left: by * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-svr-prev]')) step(-1);
      if (event.target.closest('[data-svr-next]')) step(1);
    });
    scroll.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    syncArrows();

    /* ---- player ---- */

    function paintPlaque(i) {
      if (!plaque) return;
      var d = cards[i].dataset;
      plaque.innerHTML =
        '<a class="svr-player__link" href="' + esc(d.url) + '">' +
          '<span class="svr__thumb">' +
            (d.image ? '<img src="' + esc(d.image) + '" alt="">' : '') +
          '</span>' +
          '<span class="svr__meta">' +
            '<span class="svr__name">' + esc(d.title) + '</span>' +
            '<span class="svr__price">' + esc(d.price) + '</span>' +
          '</span>' +
        '</a>' +
        (d.variant
          ? '<button type="button" class="svr-player__add" data-quick-add ' +
            'data-variant-id="' + esc(d.variant) + '" ' +
            'aria-label="' + esc(strings.addToCart || 'Add to cart') + '">+</button>'
          : '');
    }

    /* The stage was a fixed 9:16, which letterboxed every landscape film with
       black bands. Size it to the video's own ratio instead, capped so it still
       fits the viewport in both directions. */
    function fitStage() {
      var video = videoOf(active);
      if (!video || !video.videoWidth || !video.videoHeight) return;
      stage.style.setProperty('--svr-ar', (video.videoWidth / video.videoHeight).toFixed(4));
      stage.classList.add('is-sized');
    }

    function onProgress() {
      var video = videoOf(active);
      if (!bar || !video || !video.duration) return;
      bar.style.width = (video.currentTime / video.duration * 100) + '%';
    }

    function detach(i) {
      var video = videoOf(i);
      if (!video) return;
      video.removeEventListener('timeupdate', onProgress);
      video.pause();
      video.muted = true;              // never let an unmuted film return to the rail
      video.controls = false;
      var frame = frameOf(i);
      if (frame) frame.appendChild(video);
    }

    function show(i) {
      if (i < 0) i = cards.length - 1;
      if (i >= cards.length) i = 0;
      if (active === i) return;

      if (active > -1) detach(active);
      active = i;
      stage.classList.remove('is-sized');

      var video = videoOf(i);
      if (!video) return;
      stage.appendChild(video);
      paintPlaque(i);
      if (bar) bar.style.width = '0%';

      // A click is a user gesture, so sound is allowed here even though the
      // rail's autoplay was not. Re-muted on the way out, in detach().
      video.muted = false;
      if (sound) {
        sound.setAttribute('aria-pressed', 'true');
        sound.setAttribute('aria-label', strings.soundOff || 'Mute');
      }
      video.addEventListener('timeupdate', onProgress);
      video.addEventListener('loadedmetadata', fitStage);
      fitStage();
      video.play().catch(function () {});

      // Keep the rail's own scroll position on the film being watched, so
      // closing does not leave the reader somewhere else.
      cards[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
    }

    function open(i) {
      player.classList.add('is-open');
      player.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      if (steps) steps.hidden = cards.length < 2;
      show(i);
      var x = player.querySelector('.lightbox__x');
      if (x) x.focus();
    }

    function close() {
      if (active > -1) detach(active);
      active = -1;
      player.classList.remove('is-open');
      player.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      if (plaque) plaque.innerHTML = '';
      // The observer only reacts to intersection changes, so nudge the visible
      // films back into playing rather than waiting for a scroll.
      cards.forEach(function (card, i) {
        var video = videoOf(i);
        if (!video || reduceMotion) return;
        var r = card.getBoundingClientRect();
        if (r.right > 0 && r.left < window.innerWidth) video.play().catch(function () {});
      });
    }

    cards.forEach(function (card, i) {
      var opener = card.querySelector('[data-svr-open]');
      if (opener) opener.addEventListener('click', function () { open(i); });
    });

    player.addEventListener('click', function (event) {
      if (event.target.closest('[data-svr-close]')) { close(); return; }

      var stepper = event.target.closest('[data-svr-step]');
      if (stepper) { show(active + parseInt(stepper.dataset.svrStep, 10)); return; }

      if (event.target.closest('[data-svr-sound]')) {
        // stopPropagation is not enough here — the sound button sits over the
        // stage, and without handling it first the click reaches the scrim.
        event.stopPropagation();
        var video = videoOf(active);
        if (!video) return;
        video.muted = !video.muted;
        sound.setAttribute('aria-pressed', String(!video.muted));
        sound.setAttribute('aria-label', video.muted
          ? (strings.soundOn || 'Unmute') : (strings.soundOff || 'Mute'));
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!player.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') show(active + 1);
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') show(active - 1);
    });
  })();

  /* ------------------------------------------------------ Recently viewed */

  /* Same shape as the wishlist: full row objects in localStorage, so the rail
     paints with no fetch. Records the current product, then shows the others. */

  (function recentlyViewed() {
    var KEY = 'waqt:viewed';
    var MAX = 12;                       // stored, independent of how many show

    var root = document.querySelector('[data-recently-viewed]');
    var rail = root && root.querySelector('[data-rv-rail]');
    if (!root || !rail) return;

    function read() {
      try {
        var list = JSON.parse(localStorage.getItem(KEY)) || [];
        return list.filter(function (item) { return item && typeof item === 'object' && item.id; });
      } catch (e) {
        return [];                      // private mode: no history, no rail
      }
    }

    function esc(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        .replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    var current = root.dataset.currentId || '';

    // Record first. Dedupe by id and unshift, so revisiting a product moves it
    // back to the front instead of adding a second entry.
    if (current) {
      var list = read().filter(function (item) { return String(item.id) !== String(current); });
      list.unshift({
        id:      current,
        variant: root.dataset.currentVariant || '',
        title:   root.dataset.currentTitle || '',
        url:     root.dataset.currentUrl || '#',
        image:   root.dataset.currentImage || '',
        price:   root.dataset.currentPrice || ''
      });
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
    }

    /* Deliberately lighter than the wishlist card: heart only, no quick-add and
       no type badge. This is a way back to something already seen, not a second
       place to buy. Brand is skipped too — single-brand store, so vendor would
       print the shop name on every card. */
    function cardMarkup(item) {
      return '<figure class="card">' +
        '<div class="card__media">' +
          '<a href="' + esc(item.url) + '" class="card__media-link">' +
            '<div class="card__images">' +
              (item.image
                ? '<img class="card__img card__img--primary" src="' + esc(item.image) +
                  '" alt="' + esc(item.title) + '" loading="lazy">'
                : '') +
            '</div>' +
          '</a>' +
          '<button type="button" class="card__save" data-wish="' + esc(item.id) + '" ' +
            'data-wish-variant="' + esc(item.variant) + '" data-wish-title="' + esc(item.title) + '" ' +
            'data-wish-url="' + esc(item.url) + '" data-wish-image="' + esc(item.image) + '" ' +
            'data-wish-price="' + esc(item.price) + '" ' +
            'aria-pressed="false" aria-label="' + esc(strings.wishlistSave || 'Save') + '">' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
            '<path d="M12 20s-7-4.5-7-9.2A4 4 0 0 1 12 8a4 4 0 0 1 7 2.8C19 15.5 12 20 12 20z"/></svg>' +
          '</button>' +
        '</div>' +
        '<figcaption class="card__body">' +
          '<a href="' + esc(item.url) + '" class="card__name h-card-sm">' + esc(item.title) + '</a>' +
          '<span class="price-row price-row--plain"><span class="price-row__now">' +
            esc(item.price) + '</span></span>' +
        '</figcaption>' +
      '</figure>';
    }

    var limit = parseInt(root.dataset.limit, 10) || 8;
    var show = read().filter(function (item) {
      return String(item.id) !== String(current);
    }).slice(0, limit);

    if (!show.length) return;           // nothing to pick up: stays hidden

    rail.innerHTML = show.map(cardMarkup).join('');
    root.hidden = false;

    // Let the wishlist controller stamp saved state onto the new hearts.
    document.dispatchEvent(new Event('waqt:resync'));
  })();

  /* ---------------------------------------------------------- Reviews paging */

  /* Mobile turns the marquee into a vertical list, so it needs paging. Desktop
     is untouched: the controller exits above 900px and puts everything back if
     the viewport crosses the breakpoint. */

  (function reviewsPaging() {
    var viewport = document.querySelector('[data-reviews-step]');
    var button = document.querySelector('[data-reviews-more]');
    if (!viewport || !button) return;

    var step = parseInt(viewport.dataset.reviewsStep, 10) || 4;

    var reviews = Array.prototype.slice.call(viewport.querySelectorAll('[data-review]'));

    var mobile = window.matchMedia('(max-width: 900px)');
    var shown = step;

    function paint() {
      if (!mobile.matches) {
        // Desktop: strip every trace of paging so the marquee runs whole.
        reviews.forEach(function (el) { el.classList.remove('is-shown'); });
        button.hidden = true;
        return;
      }

      reviews.forEach(function (el, i) {
        el.classList.toggle('is-shown', i < shown);
      });

      button.hidden = shown >= reviews.length;
    }

    button.addEventListener('click', function () {
      shown += step;
      paint();

      // Move focus to the first newly revealed card so a keyboard or screen
      // reader user lands on what just appeared rather than at the button.
      var next = reviews[shown - step];
      if (next) {
        next.setAttribute('tabindex', '-1');
        next.focus({ preventScroll: true });
      }
    });

    mobile.addEventListener('change', function () {
      shown = step;
      paint();
      syncArrows();
    });

    /* ---- Desktop: the rail is driven by the arrows, not by an animation ---- */

    var track = viewport.querySelector('[data-reviews-track]');
    var prev = viewport.querySelector('[data-reviews-prev]');
    var next = viewport.querySelector('[data-reviews-next]');

    function syncArrows() {
      if (!track || !prev || !next) return;

      // Nothing to scroll (or stacked on mobile) — both arrows go away.
      var scrollable = !mobile.matches && track.scrollWidth - track.clientWidth > 2;
      var max = track.scrollWidth - track.clientWidth;

      prev.disabled = !scrollable || track.scrollLeft <= 1;
      next.disabled = !scrollable || track.scrollLeft >= max - 1;
    }

    if (track && prev && next) {
      // One card plus its gap, so a click always lands a card on the edge
      // rather than leaving a sliver behind.
      function stepBy(direction) {
        var card = reviews[0];
        var width = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;

        track.scrollBy({
          left: direction * width,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }

      // The scroll listener below is the main driver, but a click must not
      // depend on scroll events firing to leave the arrows in the right state —
      // re-check once the smooth scroll has had time to land.
      function settle() {
        setTimeout(syncArrows, 60);
        setTimeout(syncArrows, 500);
      }

      prev.addEventListener('click', function () { stepBy(-1); settle(); });
      next.addEventListener('click', function () { stepBy(1); settle(); });

      track.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows, { passive: true });
    }

    paint();
    syncArrows();
  })();

  /* ------------------------------------------------------------ Sticky video */

  /* A corner reel that holds its place through the scroll. Not a modal: it
     never locks scrolling, never covers the page, and takes no focus. */

  class StickyVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.svid__video');
      if (!this.video) return;

      this.key = this.dataset.key;
      this.once = this.dataset.once === 'true';

      if (this.once && this.dismissed()) {
        this.remove();
        return;
      }

      this.querySelectorAll('[data-svid-close]').forEach(function (el) {
        el.addEventListener('click', this.close.bind(this));
      }, this);

      var sound = this.querySelector('[data-svid-sound]');
      if (sound) sound.addEventListener('click', this.toggleSound.bind(this));

      this.frame = this.querySelector('.svid__frame');
      this.box = this.querySelector('[data-svid-lightbox]');
      this.stage = this.querySelector('[data-svid-stage]');

      var open = this.querySelector('[data-svid-open]');
      if (open && this.box) open.addEventListener('click', this.expand.bind(this));

      if (this.box) {
        this.box.querySelectorAll('[data-svid-lightbox-close]').forEach(function (el) {
          el.addEventListener('click', this.shrink.bind(this));
        }, this);
      }

      this.onEsc = function (event) {
        if (event.key === 'Escape' && this.box && this.box.classList.contains('is-open')) this.shrink();
      }.bind(this);
      document.addEventListener('keydown', this.onEsc);

      var delay = parseInt(this.dataset.delay, 10);
      this.timer = setTimeout(this.show.bind(this), isNaN(delay) ? 1000 : delay);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onEsc);
      document.body.classList.remove('has-sticky-video');
      document.body.classList.remove('no-scroll');
    }

    dismissed() {
      try { return sessionStorage.getItem(this.key) === '1'; }
      catch (e) { return false; }          // private mode: just show it
    }

    /* The same <video> element is MOVED between the corner and the stage.
       Cloning would re-download the file and restart playback; moving keeps
       both the buffer and the position. */
    expand() {
      if (!this.box || !this.stage) return;

      this.stage.appendChild(this.video);
      this.video.controls = true;
      this.box.classList.add('is-open');
      this.box.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');

      // A click is a user gesture, so sound is permitted here even though
      // autoplay in the corner was not.
      this.video.muted = false;
      this.syncSound();
      this.video.play().catch(function () {});

      var close = this.box.querySelector('.lightbox__x');
      if (close) close.focus();
    }

    shrink() {
      if (!this.box) return;

      this.box.classList.remove('is-open');
      this.box.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');

      // Back to the corner, muted again — an unmuted reel following you down
      // the page is the thing everyone hates about these.
      this.frame.insertBefore(this.video, this.frame.firstChild);
      this.video.controls = false;
      this.video.muted = true;
      this.classList.remove('is-paused');
      this.syncSound();
      this.video.play().catch(function () {});
    }

    show() {
      this.classList.add('is-mounted');
      // The WhatsApp float shares this corner; move it aside while we are here.
      document.body.classList.add('has-sticky-video');

      /* Autoplay only runs muted, and play() still rejects under low-power or
         data-saver. Do NOT answer that with native controls: a scrubber inside
         a 180px corner is unusable, and setting it here races the shrink()
         that clears it. Mark it paused instead — the expand affordance already
         invites the tap, and the big player has real controls. */
      this.video.muted = true;
      var attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () { this.classList.add('is-paused'); }.bind(this));
      }

      // Next frame, so the entrance transition has a state to move from.
      var self = this;
      setTimeout(function () { self.classList.add('is-in'); }, 20);
    }

    close() {
      this.classList.remove('is-in');
      this.video.pause();
      if (this.once) this.remember();

      var self = this;
      setTimeout(function () {
        self.classList.remove('is-mounted');
        document.body.classList.remove('has-sticky-video');
      }, 320);
    }

    remember() {
      try { sessionStorage.setItem(this.key, '1'); } catch (e) {}
    }

    toggleSound(event) {
      // Without this the click reaches the expand layer underneath and the
      // player opens instead of the sound toggling.
      if (event) event.stopPropagation();

      this.video.muted = !this.video.muted;
      if (this.video.paused) this.video.play().catch(function () {});
      this.syncSound();
    }

    syncSound() {
      var on = !this.video.muted;
      var button = this.querySelector('[data-svid-sound]');
      var label = this.querySelector('[data-svid-sound-text]');
      var icon = this.querySelector('.svid__sound-icon');

      if (button) button.setAttribute('aria-pressed', String(on));
      if (label) label.textContent = on ? (strings.soundOff || 'Sound off') : (strings.soundOn || 'Sound on');
      if (icon) icon.classList.toggle('is-on', on);
    }
  }

  customElements.define('sticky-video', StickyVideo);

  /* ------------------------------------------------------------ Zoom lightbox */

  (function pdpZoom() {
    var box = document.querySelector('[data-zoom]');
    if (!box) return;

    var stage   = box.querySelector('[data-zoom-img]');
    var counter = box.querySelector('[data-zoom-count]');
    var shots   = Array.prototype.slice.call(document.querySelectorAll('[data-zoom-open]'));
    if (!stage || !shots.length) return;

    var index = 0;
    var lastFocus = null;

    function show(i) {
      // Wrap, so the arrows never dead-end.
      index = (i + shots.length) % shots.length;
      var shot = shots[index];

      stage.src = shot.dataset.full;
      stage.alt = shot.dataset.alt || '';
      if (counter) counter.textContent = (index + 1) + ' / ' + shots.length;
    }

    function open(i) {
      lastFocus = document.activeElement;
      show(i);
      box.classList.add('is-open');
      box.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
    }

    function close() {
      box.classList.remove('is-open');
      box.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    shots.forEach(function (button, i) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        open(i);
      });
    });

    box.querySelectorAll('[data-zoom-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    box.querySelectorAll('[data-zoom-step]').forEach(function (el) {
      el.addEventListener('click', function () {
        show(index + parseInt(el.dataset.zoomStep, 10));
      });
    });

    document.addEventListener('keydown', function (event) {
      if (!box.classList.contains('is-open')) return;

      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowRight') show(index + 1);
      else if (event.key === 'ArrowLeft') show(index - 1);
    });

    // Swipe. The arrows are hidden on mobile, so this is the only way through
    // the set there — it is not a nicety.
    var startX = null;
    var startY = null;

    box.addEventListener('touchstart', function (event) {
      startX = event.changedTouches[0].clientX;
      startY = event.changedTouches[0].clientY;
    }, { passive: true });

    box.addEventListener('touchend', function (event) {
      if (startX === null) return;

      var dx = event.changedTouches[0].clientX - startX;
      var dy = event.changedTouches[0].clientY - startY;
      startX = null;

      // Ignore anything that reads as a vertical drag rather than a swipe.
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
      show(index + (dx < 0 ? 1 : -1));
    }, { passive: true });
  })();

  /* ---------------------------------------------------------------- Facets */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      var run = this.debounce(this.apply.bind(this), 400);
      this.form.addEventListener('input', run);
      this.form.addEventListener('change', function (event) {
        if (event.target.name === 'sort_by') run();
      });
    }

    debounce(fn, wait) {
      var timer;
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }

    apply() {
      var params = new URLSearchParams(new FormData(this.form));
      // Drop empty price inputs so they don't produce ?filter.v.price.gte=
      Array.from(params.entries()).forEach(function (pair) {
        if (pair[1] === '') params.delete(pair[0]);
      });

      var url = this.dataset.resultsUrl.split('?')[0] + '?' + params.toString();

      fetch(url)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          ['[data-results]', '[data-facets]'].forEach(function (selector) {
            var fresh = parsed.querySelector(selector);
            var live = document.querySelector(selector);
            if (fresh && live) live.innerHTML = fresh.innerHTML;
          });
          window.history.pushState({}, '', url);
        });
    }
  }

  customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------------------- Cart */

  /* Cart requests ask Shopify to render the drawer section in the SAME
     response (the `sections` parameter). Adding an item used to cost three
     sequential round trips — add.js, then cart.js, then ?section_id= — and the
     drawer only opened after all three. That was the two-to-three second wait.
     It is now one request, and the drawer opens before it is even sent. */

  var CART_SECTION = 'cart-drawer';

  function cartDrawerEl() {
    return document.querySelector('[data-drawer="cart"]');
  }

  function setCartBusy(busy) {
    var drawer = cartDrawerEl();
    if (drawer) drawer.classList.toggle('is-busy', !!busy);
  }

  // Paint a section's rendered HTML into the live drawer, preserving open state.
  function applyDrawerHTML(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-drawer="cart"]');
    var live = cartDrawerEl();
    if (!fresh || !live) return;

    var wasOpen = live.classList.contains('is-open');
    live.innerHTML = fresh.innerHTML;
    if (wasOpen) live.classList.add('is-open');

    // Totals ride inside the section, so the badge needs no extra request.
    var state = live.querySelector('[data-cart-state]');
    if (!state) return;

    var count = parseInt(state.dataset.count, 10) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (element) {
      element.textContent = count;
      element.classList.toggle('is-hidden', count === 0);
    });
  }

  // Fallback for anything that still has only a cart object to work from.
  function refreshCartDrawer() {
    return fetch(routes.cart_url + '?section_id=' + CART_SECTION)
      .then(function (response) { return response.text(); })
      .then(applyDrawerHTML);
  }

  function syncCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (element) {
      element.textContent = cart.item_count;
      element.classList.toggle('is-hidden', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-subtotal]').forEach(function (element) {
      element.textContent = money(cart.total_price);
    });
  }

  function afterCartChange(cart, openDrawer) {
    syncCartCount(cart);

    var html = cart.sections && cart.sections[CART_SECTION];
    if (html) {
      applyDrawerHTML(html);
      if (openDrawer) Drawers.open('cart');
      return Promise.resolve();
    }

    return refreshCartDrawer().then(function () {
      if (openDrawer) Drawers.open('cart');
    });
  }

  function addVariant(variantId, quantity, button) {
    var original = button ? button.textContent : null;
    if (button) {
      button.disabled = true;
      button.textContent = '…';
    }

    // Open first. The drawer already holds the previous cart, so there is
    // something to look at while the request is in flight.
    Drawers.open('cart');
    setCartBusy(true);

    return postJSON(routes.cart_add_url + '.js', {
      items: [{ id: variantId, quantity: quantity || 1 }],
      sections: CART_SECTION
    })
      .then(function (data) {
        var html = data.sections && data.sections[CART_SECTION];
        if (html) applyDrawerHTML(html);
        else return refreshCartDrawer();
      })
      .catch(function (error) {
        Drawers.close('cart');
        window.alert(error.message || strings.cartError);
      })
      .finally(function () {
        setCartBusy(false);
        if (button) {
          button.disabled = false;
          button.textContent = original;
        }
      });
  }

  function changeLine(key, quantity) {
    setCartBusy(true);

    return postJSON(routes.cart_change_url + '.js', {
      id: key,
      quantity: quantity,
      sections: CART_SECTION
    })
      .then(function (cart) {
        return afterCartChange(cart, false).then(function () {
          if (document.body.classList.contains('template-cart')) window.location.reload();
        });
      })
      .catch(function (error) { window.alert(error.message || strings.cartError); })
      .finally(function () { setCartBusy(false); });
  }

  document.addEventListener('click', function (event) {
    var quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      addVariant(quickAdd.dataset.variantId, 1, quickAdd);
      return;
    }

    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.dataset.key, 0);
      return;
    }

    var recover = event.target.closest('[data-recover-toggle]');
    if (recover) {
      event.preventDefault();
      var panel = document.getElementById('recover');
      if (panel) {
        panel.hidden = false;
        panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      return;
    }

    // Shopify address deletion needs a real form POST with _method=delete.
    var del = event.target.closest('[data-address-delete]');
    if (del) {
      event.preventDefault();
      if (!window.confirm(del.dataset.confirm)) return;

      var form = document.createElement('form');
      form.method = 'post';
      form.action = del.dataset.url;
      form.innerHTML = '<input type="hidden" name="_method" value="delete">';
      document.body.appendChild(form);
      form.submit();
      return;
    }

    var step = event.target.closest('[data-qty-step]');
    if (step) {
      event.preventDefault();
      var input = step.parentElement.querySelector('[data-qty-input], input');
      if (!input) return;

      var min = parseInt(input.min || '0', 10);
      var value = Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyStep, 10));
      if (value === parseInt(input.value, 10)) return;

      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-qty-input]');
    if (!input) return;
    changeLine(input.dataset.key, parseInt(input.value, 10));
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();

    var data = Object.fromEntries(new FormData(form));
    var button = form.querySelector('[data-add-to-cart]');
    addVariant(data.id, parseInt(data.quantity, 10) || 1, button);
  });
})();
